/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react';
import axios from 'axios';

import { jsx, css } from '@emotion/react';
import PageHeader from '@atlaskit/page-header';
import { Grid, GridColumn, PageWrapper } from '../../components/page';
import Spinner from '@atlaskit/spinner';
import { queryCache, useQuery } from 'react-query';
import { fetchVatCodes } from '../../components/select/VatCodeSelect';
import api from '../../libs/api';
import { useHref, useNavigate, useParams } from 'react-router';
import SaleInvoicePreview from './SaleInvoicePreview';
import { useCurrentCompany } from '../auth/CurrentCompany';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button, { ButtonGroup } from '@atlaskit/button';
import { FormattedMessage } from 'components/i18n';
import { DropdownLinkItem, MoreDropdownMenu } from '../../components/dropdown-menu';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Link, Route, Routes } from 'react-router-dom';
import SendEmailPage from './SendEmailPage';
import { RecurringSale, Sale } from './types';
import useConfirm from 'components/modal-dialog/useConfirm';
import { BankAccount } from '../bank/types';
import { useNotifications } from '../../components/notification';
import SaleStatus from './components/SaleStatus';
import SaleOverview from './components/SaleOverview';
import { useAuth } from '../auth';
import { useIntl } from 'components/i18n';
import useCompanyNavigation from "../auth/useCompanyNavigation";
const FileSaver = require('file-saver');

type Props = {}
// export const fetchSale = async (key: string, id: string) =>  api.get(`/sales/${id}`);
export const getSaleById = async (key: string, id: string) => {
  return (await api.get(`/Sales/${id}`, {params: {filter: {include: ['customer', 'Attachment', 'events']}}}) as Sale);
};

export const getRecurringSaleById = async (key: string, id: string) => api.get(`/recurring-sales/${id}`, {params: {filter: {include: ['customer']}}});
export const deleteSale = async (id: string,) => api.delete(`/Sales/${id}`);

const SalePage: React.FC<Props> = (props) => {
  const { isBookkeeper } = useAuth();
  const company = useCurrentCompany();
  const navigate = useCompanyNavigation();
  const salesHref = useHref('../');
  const intl = useIntl();
  const { id } = useParams();
  const { data: sale, error, isLoading } = useQuery(['sale', id], getSaleById);
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const { confirmDelete } = useConfirm();

  const handleDownload = useCallback(async() => {
   const data = (await api.get(`Sales/${sale?.id}/export`, {
  })) as any;
    console.log('data.url',data.url);
    const response = (await axios.get(data.pdf,
    {responseType: 'arraybuffer'}
    )) as any;
    const blob = new Blob([response.data]);
    FileSaver.saveAs(blob, data.fileName);
  },[sale?.id]);

  const handleDuplicate = useCallback(async() => {
    const duplicatedSale = (await api.post(`Sales/${sale?.id}/duplicate`, {
    })) as any;
    navigate(`/sale/invoices/${duplicatedSale.id}/edit`);
    },[sale?.id]);

  const handleMarkSent = useCallback(async() => {
    try {
      const result = (await api.post(`Sales/${sale?.id}/mark-sent`, {}));
      showSuccessNotification(
        <FormattedMessage
          id="sale.SalePage.notifications.markSent.success.title`"
          defaultMessage="Marked as sent"
        />
      );
    } catch (e) {
      showErrorNotification(e.message);
    }
    queryCache.invalidateQueries(['sale', sale.id]);
  },[sale?.id]);

  const handleCreditInvoice = useCallback(async() => {
    const creditInvoice = (await api.post(`Sales/${sale?.id}/credit`, {
    })) as any;
    navigate(`/sale/invoices/${creditInvoice.id}/edit`);
  },[sale?.id]);


  const handleDelete = useCallback(async () => {
    confirmDelete({
      title: (
        <FormattedMessage
          id="sale.SalePage.modals.delete.title`"
          defaultMessage="Delete this invoice?"
        />
      ),
      content: (
        <FormattedMessage
          id="sale.SalePage.modals.delete.content`"
          defaultMessage="You are about to delete invoice with invoice number {invoiceNumber}."
          values={{ invoiceNumber: sale?.invoiceNumber }}
        />
      ),
      onConfirm: async () => {
        try {
          await deleteSale(sale.id);
          showSuccessNotification(
            <FormattedMessage
              id="sale.SalePage.notifications.delete.success.title`"
              defaultMessage="Sale deleted"
            />
          );
        } catch (e) {
          showErrorNotification(e.message);
        }
        queryCache.invalidateQueries('sales');
        // navigate('../')
        navigate(-1) // FIXME change to ../ and prevent bounce back from /sales
      },
    });
  }, [sale?.id]);

  const actionsContent = useMemo(
    () => {
      if (!sale) return <span/>;
      return (
        <ButtonGroup>
          <Link to="email" replace>
            <Button
              appearance="primary"
              // onClick={() => connectKlarna()}
            >
              <FormattedMessage
                id="sale.SalePage.actions.sendEmail"
                defaultMessage="Send email"
              />
            </Button>
          </Link>
          <Button
            onClick={handleDownload}
          >
            <FormattedMessage
              id="sale.SalePage.actions.downloadPdf"
              defaultMessage="Download PDF"
            />
          </Button>
          {(sale.status === 'draft' || isBookkeeper) && <Link to="edit">
            <Button
              // onClick={() => connectKlarna()}
            >
              <FormattedMessage
                id="sale.SalePage.actions.edit"
                defaultMessage="Edit invoice"
              />
            </Button>
          </Link>}
          <MoreDropdownMenu>
            <DropdownItemGroup>
              {sale.status === 'draft' && <DropdownItem
                onClick={handleMarkSent}
              >
                <FormattedMessage
                  id="sale.SalePage.actions.markSent"
                  defaultMessage="Mark as sent"
                />
              </DropdownItem>
              }
              <DropdownItem
                onClick={handleDuplicate}
              >
                <FormattedMessage
                  id="sale.SalePage.actions.duplicate"
                  defaultMessage="Duplicate invoice"
                />
              </DropdownItem>
              {/*<DropdownItem*/}
              {/*  // onClick={handleAddCustomAccount}*/}
              {/*>*/}
              {/*  <FormattedMessage*/}
              {/*    id="sale.SalePage.actions.duplicateAsRepeating"*/}
              {/*    defaultMessage="Make repeating invoice"*/}
              {/*  />*/}
              {/*</DropdownItem>*/}
              {sale.status !== 'draft' &&
              <DropdownItem
                onClick={handleCreditInvoice}
              >
                <FormattedMessage
                  id="sale.SalePage.actions.creditInvoice"
                  defaultMessage="Make credit invoice"
                />
              </DropdownItem>
              }
              {(sale.status === 'draft' || isBookkeeper) && <DropdownItem
                onClick={handleDelete}
              >
                <FormattedMessage
                  id="sale.SalePage.actions.delete"
                  defaultMessage="Delete invoice"
                />
              </DropdownItem>}
            </DropdownItemGroup>
          </MoreDropdownMenu>
        </ButtonGroup>
      )
    },
    [sale]
  );

  if(isLoading || !sale) {
    return (<PageWrapper fullWidth>
      <Spinner/>
    </PageWrapper>)
  }

 const invoicesPath = intl.formatMessage({
    id: 'sale.SalePages.invoices.path',
    defaultMessage: 'Invoices',
  });

  return (
    <PageWrapper fullWidth>
      <Routes>
        <Route path="email" element={<SendEmailPage/>}/>
      </Routes>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs onExpand={() => {}}>
            <BreadcrumbsItem text={invoicesPath} key={invoicesPath} href={salesHref}/>
            <BreadcrumbsItem
              text={sale.invoiceNumber || ''}
              key={sale.invoiceNumber || ''}
            />
          </Breadcrumbs>
        }
        actions={actionsContent}
      >
        <span css={css`display: flex; align-items: center;`}>
           {sale.isCreditInvoice ?
             <FormattedMessage
               id="sale.SalePage.title.creditInvoice"
               defaultMessage="Credit invoice {invoiceNumber}"
               values={sale}
             /> :
             <FormattedMessage
               id="sale.SalePage.title.invoice"
               defaultMessage="Invoice {invoiceNumber}"
               values={sale}
             />}
          <div css={css`margin-left: 0.25rem;`}><SaleStatus invoice={sale}/></div>
        </span>

      </PageHeader>
      <Grid layout="fluid" spacing="comfortable">
        <GridColumn medium={9} >
      <SaleInvoicePreview invoiceSettings={company?.invoiceSettings} invoice={sale} scale={0.85}/>
        </GridColumn>
        <GridColumn medium={3}>
          <SaleOverview sale={sale}/>
        </GridColumn>
       </Grid>
    </PageWrapper>
  );
};

export default SalePage;
